import {
    LEGAL_MEASURE_TYPE_OPTIONS,
    POLE_OPTIONS,
    STATUS_OPTIONS,
    type SelectOption,
} from '../../../../types/LegalCase';

// Modelo data-driven dos filtros da grid. Cada `FilterDef` carrega TUDO que o filtro precisa:
// renderização (drawer + chip), estado e o mapeamento para o parâmetro da API. Adicionar um
// filtro numa task futura (acordo, penhora, CPC 25) é **uma entrada em LEGAL_CASE_FILTER_DEFS** —
// sem tocar no hook, no componente ou no request. É o mesmo espírito do SECTIONS do detalhe.

export type FilterKind = 'select' | 'toggle' | 'choice';

export interface FilterDef {
    // Chave estável do filtro (usada no estado, no chip e como default do parâmetro de request).
    id: string;
    // Rótulo do grupo no drawer e prefixo do chip.
    label: string;
    kind: FilterKind;
    // Nome do parâmetro enviado ao getLegalCases. Default: o próprio `id`.
    queryKey?: string;
    // Converte o valor do estado para o valor de request (ex.: string "15" → number 15).
    // Devolver undefined omite o parâmetro. Default: usa o valor como está (se não-vazio).
    toQuery?: (value: string | boolean) => string | number | boolean | undefined;
    // select: dropdown de opção única (com opção "qualquer"). choice: botões mutuamente
    // exclusivos (ex.: horizontes de prazo). toggle: liga/desliga (checkbox).
    options?: SelectOption[];
    // Placeholder da opção vazia no select.
    emptyLabel?: string;
    // Como renderizar o valor no chip (ex.: mapear "15" → "vencendo em 15 dias").
    formatValue?: (value: string) => string;
}

// Um filtro ativo, já resolvido para exibição no chip.
export interface ActiveFilter {
    id: string;
    label: string;
    displayValue: string;
}

// Valor de cada filtro no estado. string vazia / false = inativo.
export type FilterValues = Record<string, string | boolean>;

// Horizontes rápidos para o filtro de prazo vencendo (a demo pede "próximos 15 dias").
const DEADLINE_HORIZON_OPTIONS: SelectOption[] = [
    { value: '7', label: '7 dias' },
    { value: '15', label: '15 dias' },
    { value: '30', label: '30 dias' },
];

const toPositiveInt = (value: string | boolean): number | undefined => {
    const parsed = parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : undefined;
};

// Fonte única dos filtros da listagem de casos. O hook consome daqui (para montar os
// parâmetros de request — não depende das options). O `responsibleFirm` recebe as opções em
// runtime (escritórios cadastrados) na página, via buildLegalCaseFilterDefs.
export const LEGAL_CASE_FILTER_DEFS: FilterDef[] = [
    { id: 'pole', label: 'Polo', kind: 'select', options: POLE_OPTIONS, emptyLabel: 'Qualquer polo' },
    { id: 'status', label: 'Situação', kind: 'select', options: STATUS_OPTIONS, emptyLabel: 'Qualquer situação' },
    { id: 'measureType', label: 'Medida', kind: 'select', options: LEGAL_MEASURE_TYPE_OPTIONS, emptyLabel: 'Qualquer medida' },
    { id: 'measurePending', label: 'Só medidas pendentes', kind: 'toggle' },
    {
        id: 'deadlineWithinDays',
        label: 'Prazo vencendo',
        kind: 'choice',
        options: DEADLINE_HORIZON_OPTIONS,
        formatValue: (v) => `vencendo em ${v} dias`,
        toQuery: toPositiveInt,
    },
    {
        id: 'responsibleFirmId',
        label: 'Escritório responsável',
        kind: 'select',
        options: [],
        emptyLabel: 'Qualquer escritório',
    },
];

// Defs para o drawer, com as opções de escritório injetadas em runtime (a lista de
// escritórios cadastrados só existe no cliente). O request usa LEGAL_CASE_FILTER_DEFS direto.
export const buildLegalCaseFilterDefs = (
    firmOptions: SelectOption[],
): FilterDef[] =>
    LEGAL_CASE_FILTER_DEFS.map((def) =>
        def.id === 'responsibleFirmId' ? { ...def, options: firmOptions } : def,
    );

// Estado inicial (tudo inativo) derivado das defs: toggle→false, resto→''.
export const emptyFilterValues = (defs: FilterDef[]): FilterValues => {
    const values: FilterValues = {};
    for (const def of defs) values[def.id] = def.kind === 'toggle' ? false : '';
    return values;
};

// Monta os parâmetros de request a partir dos valores atuais e das defs. Omite os inativos e
// aplica o `toQuery` de cada filtro. É o que fecha o ciclo data-driven: nada de mapeamento
// id-by-id no hook.
export const toQueryParams = (
    defs: FilterDef[],
    values: FilterValues,
): Record<string, string | number | boolean> => {
    const params: Record<string, string | number | boolean> = {};
    for (const def of defs) {
        const value = values[def.id];
        const isInactive = def.kind === 'toggle' ? value !== true : !value;
        if (isInactive) continue;
        const queryValue = def.toQuery ? def.toQuery(value) : value;
        if (queryValue === undefined) continue;
        params[def.queryKey || def.id] = queryValue;
    }
    return params;
};

// Deriva os filtros ativos (para os chips) a partir das definições e dos valores atuais.
export const deriveActiveFilters = (
    defs: FilterDef[],
    values: FilterValues,
): ActiveFilter[] => {
    const active: ActiveFilter[] = [];
    for (const def of defs) {
        const value = values[def.id];
        if (def.kind === 'toggle') {
            if (value === true) active.push({ id: def.id, label: def.label, displayValue: def.label });
            continue;
        }
        if (typeof value === 'string' && value !== '') {
            const display = def.formatValue
                ? def.formatValue(value)
                : labelForValue(def, value);
            active.push({ id: def.id, label: def.label, displayValue: display });
        }
    }
    return active;
};

const labelForValue = (def: FilterDef, value: string): string => {
    const option = def.options?.find((o) => o.value === value);
    return option ? option.label : value;
};

export const countActive = (defs: FilterDef[], values: FilterValues): number =>
    deriveActiveFilters(defs, values).length;
